/**
 * FairwayBid – Angebote zu einer Anfrage
 *
 * Ein Angebot ist im Marktplatz eine Transaktion im Prozess
 * default-negotiation: Die Anfrage (das Listing) gehoert dem Club, der Haendler
 * eroeffnet die Transaktion mit seinem Angebot. Aus Sicht des Clubs sind das
 * also seine "orders".
 *
 * Dieses Modul buendelt die reine Rechenlogik: gruppieren, Betrag lesen,
 * Bestpreis finden, deutsche Statustexte. Bewusst ohne React und ohne SDK,
 * damit es sich testen laesst.
 */

// Angebote, die fuer den Club nicht mehr zaehlen.
const CLOSED_TRANSITIONS = [
  'transition/customer-reject-offer',
  'transition/operator-reject-offer',
  'transition/provider-withdraw-offer',
  'transition/reject-request',
  'transition/withdraw-request',
  'transition/operator-reject-request',
  'transition/expire-payment',
  'transition/operator-reject-from-customer-counter-offer',
  'transition/customer-reject-from-update-pending',
  'transition/provider-withdraw-from-update-pending',
];

const ACCEPTED_TRANSITIONS = [
  'transition/confirm-payment',
  'transition/accept-offer',
  'transition/provider-accept-counter-offer',
];

export const STATUS_LABELS = {
  offen: 'Offen',
  verhandlung: 'In Verhandlung',
  angenommen: 'Angenommen',
  abgelehnt: 'Abgelehnt',
};

export const getOfferStatus = tx => {
  const last = tx?.attributes?.lastTransition;
  if (!last) return 'offen';
  if (CLOSED_TRANSITIONS.includes(last)) return 'abgelehnt';
  if (ACCEPTED_TRANSITIONS.includes(last)) return 'angenommen';
  if (last.includes('counter-offer') || last.includes('update')) return 'verhandlung';
  return 'offen';
};

export const isOpenOffer = tx => {
  const s = getOfferStatus(tx);
  return s === 'offen' || s === 'verhandlung';
};

/**
 * Angebotsbetrag einer Transaktion. Die Verhandlungssumme landet als
 * Zeilenposten im Gesamtbetrag; wo der noch fehlt, rechnen wir die Posten
 * selbst zusammen.
 */
export const getOfferAmount = tx => {
  const attrs = tx?.attributes;
  const total = attrs?.payinTotal;
  if (total && typeof total.amount === 'number') {
    return { amount: total.amount, currency: total.currency };
  }
  const lineItems = attrs?.lineItems;
  if (Array.isArray(lineItems) && lineItems.length > 0) {
    const currency = lineItems.find(li => li?.lineTotal?.currency)?.lineTotal?.currency;
    const sum = lineItems.reduce(
      (acc, li) => acc + (typeof li?.lineTotal?.amount === 'number' ? li.lineTotal.amount : 0),
      0
    );
    if (currency) return { amount: sum, currency };
  }
  return null;
};

export const formatAmount = money => {
  if (!money || typeof money.amount !== 'number') return null;
  const value = money.amount / 100;
  return value.toLocaleString('de-DE', {
    style: 'currency',
    currency: money.currency || 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const listingIdOf = tx => {
  const rel = tx?.listing?.id?.uuid || tx?.relationships?.listing?.data?.id?.uuid;
  return rel || null;
};

/**
 * Gruppiert Angebote nach der Anfrage, zu der sie gehoeren.
 * @returns {Object} { [listingId]: Transaction[] }
 */
export const groupOffersByListing = (transactions = []) => {
  const out = {};
  (Array.isArray(transactions) ? transactions : []).forEach(tx => {
    const id = listingIdOf(tx);
    if (!id) return;
    if (!out[id]) out[id] = [];
    out[id].push(tx);
  });
  return out;
};

/**
 * Kennzahlen zu den Angeboten einer Anfrage.
 * @returns {{total: number, open: number, best: Object|null, bestLabel: string|null}}
 */
export const summarizeOffers = (transactions = []) => {
  const list = Array.isArray(transactions) ? transactions : [];
  const open = list.filter(isOpenOffer);
  const withAmount = open
    .map(tx => ({ tx, money: getOfferAmount(tx) }))
    .filter(entry => entry.money);

  const best = withAmount.reduce(
    (acc, entry) => (acc === null || entry.money.amount < acc.money.amount ? entry : acc),
    null
  );

  return {
    total: list.length,
    open: open.length,
    best: best ? best.tx : null,
    bestMoney: best ? best.money : null,
    bestLabel: best ? formatAmount(best.money) : null,
  };
};

/**
 * Angebote sortiert, wie ein Club sie vergleichen will: guenstigstes zuerst,
 * Angebote ohne Betrag ans Ende. Bewusst kein Vorrang fuer bezahlte Tarife –
 * ein Preisvergleich, der Reihenfolge verkauft, waere fuer Clubs wertlos.
 */
export const sortOffersForComparison = (transactions = []) => {
  const list = Array.isArray(transactions) ? [...transactions] : [];
  return list.sort((a, b) => {
    const ma = getOfferAmount(a);
    const mb = getOfferAmount(b);
    if (ma && mb) return ma.amount - mb.amount;
    if (ma) return -1;
    if (mb) return 1;
    return 0;
  });
};

/**
 * Wie viele Angebote hat ein Haendler im laufenden Kalendermonat abgegeben?
 * Grundlage fuer das Kontingent aus der Mitgliedschaft.
 */
export const countOffersInMonth = (transactions = [], now = new Date()) => {
  const list = Array.isArray(transactions) ? transactions : [];
  const y = now.getFullYear();
  const m = now.getMonth();
  return list.filter(tx => {
    const created = tx?.attributes?.createdAt;
    if (!created) return false;
    const d = created instanceof Date ? created : new Date(created);
    if (Number.isNaN(d.getTime())) return false;
    return d.getFullYear() === y && d.getMonth() === m;
  }).length;
};
